import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Crown, Skull, Scroll, Activity, ArrowLeft, Sparkles, ShieldCheck, ShieldAlert, ChevronRight, Flame, Lock } from 'lucide-react';
import chancellorPortraitHead from '../assets/chancellor/specter.jpg';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = {
  DEGEN:    '#ff6464',
  ACTIVE:   '#ffaa50',
  CASUAL:   '#dcc878',
  SLEEPING: '#a0a0a0',
  DORMANT:  '#6e6e82',
};

function shortAgo(iso) {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const diff = (Date.now() - t) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s`;
    if (diff < 3600) return `${Math.floor(diff/60)}m`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h`;
    return `${Math.floor(diff/86400)}d`;
  } catch (_) { return ''; }
}

function CompactUSD(v) {
  const n = Number(v) || 0;
  if (n >= 1e9) return `$${(n/1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n/1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n/1e3).toFixed(1)}K`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  if (n >= 0.0001) return `$${n.toFixed(6)}`;
  return `$${n.toFixed(10).replace(/0+$/, '').replace(/\.$/, '')}`;
}

export default function CourtPage() {
  const [data, setData] = useState(null);
  const [oaths, setOaths] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [r1, r2] = await Promise.all([
          fetch(`${API_URL}/api/chancellor/court`),
          fetch(`${API_URL}/api/chancellor/oaths/recent?limit=8`),
        ]);
        if (!r1.ok) throw new Error('court unavailable');
        const d = await r1.json();
        if (!cancelled) setData(d);
        if (r2.ok) {
          const o = await r2.json();
          if (!cancelled) setOaths(o);
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <div className="relative min-h-screen w-full overflow-x-clip" data-testid="court-page"
      style={{ background: '#050208', color: '#e6dcc8' }}>

      <style>{`
        @keyframes court-rune-fall {
          0%   { transform: translateY(-10vh); opacity: 0; }
          15%  { opacity: 0.16; }
          88%  { opacity: 0.10; }
          100% { transform: translateY(110vh); opacity: 0; }
        }
        @keyframes court-pulse {
          0%, 100% { opacity: 0.65; }
          50% { opacity: 1; }
        }
        @keyframes court-fade-in {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes court-glint {
          0%, 100% { transform: rotate(0deg); opacity: 0.65; }
          50% { transform: rotate(180deg); opacity: 1; }
        }
      `}</style>

      {/* === ATMOSPHERE === */}
      <div className="fixed inset-0 z-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 50% 0%, rgba(60,12,20,0.6) 0%, rgba(20,4,12,0.85) 35%, rgba(8,0,4,0.95) 70%, #050208 100%)',
      }} />
      {Array.from({ length: 8 }).map((_, i) => {
        const glyphs = ['✦','☩','⚜','☉','⚝','†','⚖','◆'];
        const g = glyphs[i % glyphs.length];
        const left = (i * 13.7 + 8) % 100;
        const dur = 28 + (i * 2.7) % 14;
        const delay = -(i * 1.9) % 26;
        return (
          <span key={`run-${i}`} className="fixed font-serif select-none pointer-events-none z-0"
            style={{
              left: `${left}%`, top: '-8vh',
              fontSize: `${12 + (i % 3) * 5}px`,
              color: 'rgba(140,40,20,0.14)',
              animation: `court-rune-fall ${dur}s linear ${delay}s infinite`,
            }}>{g}</span>
        );
      })}

      {/* Sticky top bar — consistent with Chancellor / Vigil / Codex */}
      <div
        className="sticky top-0 z-20 border-b"
        style={{
          borderColor: 'rgba(220,200,120,0.18)',
          background: 'rgba(5,2,8,0.85)',
          backdropFilter: 'blur(6px)',
        }}
      >
        <div className="max-w-6xl mx-auto flex items-center px-3 sm:px-6 lg:px-10 pr-16 md:pr-6 lg:pr-10 py-3 sm:py-4">
          <Link to="/halls" data-testid="court-back-link"
            className="inline-flex items-center gap-1.5 sm:gap-2 font-mono uppercase tracking-[0.22em] sm:tracking-[0.28em] transition-all hover:scale-[1.04] shrink-0"
            style={{
              padding: '7px 11px',
              fontSize: '0.62rem',
              color: '#ffd680',
              background: 'linear-gradient(180deg, rgba(40,12,8,0.6) 0%, rgba(20,6,4,0.85) 100%)',
              border: '1px solid rgba(200,144,72,0.55)',
              borderRadius: 2,
              boxShadow: '0 0 14px rgba(200,144,72,0.28), inset 0 0 14px rgba(0,0,0,0.5)',
              textDecoration: 'none',
              textShadow: '0 0 8px rgba(200,144,72,0.5)',
            }}>
            <ArrowLeft className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">Halls of the Chancellor</span>
            <span className="sm:hidden">Halls</span>
          </Link>
        </div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-3 sm:px-6 lg:px-10 pt-6 sm:pt-8 pb-20 sm:pb-24">

        {/* === HEADER === */}
        <header className="text-center mb-8" style={{ animation: 'court-fade-in 0.8s ease-out' }}>

          <div className="flex items-center justify-center gap-4 mb-4">
            <div style={{ animation: 'court-glint 8s linear infinite', color: 'rgba(200,144,72,0.5)' }}>◆</div>
            <div className="relative" style={{ width: '64px', height: '64px' }}>
              <div className="absolute pointer-events-none rounded-full"
                style={{
                  width: '110px', height: '110px',
                  left: '50%', top: '50%',
                  transform: 'translate(-50%,-50%)',
                  background: 'radial-gradient(circle, rgba(200,144,72,0.25) 0%, rgba(120,40,20,0.12) 40%, transparent 75%)',
                }} />
              <img src={chancellorPortraitHead} alt="" draggable="false"
                className="relative w-full h-full"
                style={{ objectFit: 'cover', objectPosition: '50% 25%', borderRadius: '50%', border: '1px solid rgba(200,144,72,0.4)' }} />
            </div>
            <div style={{ animation: 'court-glint 8s linear infinite reverse', color: 'rgba(200,144,72,0.5)' }}>◆</div>
          </div>

          <h1 style={{
            fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
            fontSize: 'clamp(28px, 4vw, 44px)',
            color: '#c89048',
            letterSpacing: '0.22em',
            textShadow: '0 0 20px rgba(200,144,72,0.4), 0 0 50px rgba(220,38,38,0.2)',
            fontWeight: 700,
            lineHeight: 1.1,
          }}>
            THE COURT OF THE CHANCELLOR
          </h1>
          <p className="mt-2 text-[10px] font-mono tracking-[0.4em] uppercase"
            style={{ color: 'rgba(220,80,60,0.65)' }}>
            · where every soul is remembered ·
          </p>

          {/* Stats strip */}
          {data && (
            <div className="mt-6 inline-flex flex-wrap items-center justify-center gap-x-7 gap-y-2 px-5 py-2.5"
              style={{
                background: 'linear-gradient(180deg, rgba(20,6,8,0.7) 0%, rgba(10,2,4,0.85) 100%)',
                border: '1px solid rgba(200,144,72,0.22)',
                borderRadius: '2px',
              }}>
              <CourtStat label="souls in ledger" value={data.stats?.souls_total ?? '—'} testid="stat-souls-total" />
              <span style={{ color: 'rgba(200,144,72,0.18)' }}>·</span>
              <CourtStat label="judged 24h" value={data.stats?.souls_24h ?? '—'} testid="stat-souls-24h" />
              <span style={{ color: 'rgba(200,144,72,0.18)' }}>·</span>
              <CourtStat label="tokens called" value={data.ledger_stats?.total_calls ?? '—'} testid="stat-tokens-total" />
              <span style={{ color: 'rgba(200,144,72,0.18)' }}>·</span>
              <CourtStat label="accuracy"
                value={data.ledger_stats?.accuracy_pct != null ? `${data.ledger_stats.accuracy_pct}%` : 'pending'}
                testid="stat-accuracy" />
            </div>
          )}
        </header>

        {loading && <div className="text-center py-20 text-[11px] font-mono tracking-[0.4em] uppercase"
          style={{ color: 'rgba(200,144,72,0.45)', animation: 'court-pulse 2s ease-in-out infinite' }}>
          the court convenes...
        </div>}

        {error && <div className="text-center py-20 text-[11px] font-mono"
          style={{ color: 'rgba(220,80,60,0.7)' }}>the court doors are barred. ({error})</div>}

        {data && (
          <>
            {/* === TODAY'S DECREE === */}
            <section className="mb-10" data-testid="court-decree" style={{ animation: 'court-fade-in 0.8s ease-out 0.1s both' }}>
              <SectionLabel icon={Scroll} label="today's decree" />
              <div className="relative px-6 sm:px-10 py-8 text-center"
                style={{
                  background: 'linear-gradient(180deg, rgba(40,12,8,0.55) 0%, rgba(20,6,4,0.7) 100%)',
                  border: '1px solid rgba(200,144,72,0.32)',
                  borderRadius: '2px',
                  boxShadow: '0 0 32px rgba(140,40,20,0.18)',
                }}>
                <span aria-hidden className="absolute" style={{
                  top: '4px', left: '12px', fontSize: '60px', lineHeight: 1,
                  fontFamily: "'Cinzel Decorative', serif",
                  color: 'rgba(200,144,72,0.25)',
                }}>“</span>
                <p style={{
                  fontFamily: "'Cinzel', 'Cinzel Decorative', serif",
                  fontSize: 'clamp(15px, 2vw, 19px)',
                  color: '#e6dcc8',
                  lineHeight: 1.7,
                  letterSpacing: '0.02em',
                  textShadow: '0 0 12px rgba(120,40,20,0.18)',
                  fontStyle: 'italic',
                  maxWidth: '720px',
                  margin: '0 auto',
                }}>
                  {data.decree}
                </p>
                <span aria-hidden className="absolute" style={{
                  bottom: '4px', right: '12px', fontSize: '60px', lineHeight: 1,
                  fontFamily: "'Cinzel Decorative', serif",
                  color: 'rgba(200,144,72,0.25)',
                }}>”</span>
              </div>
            </section>

            {/* === SOVEREIGN + DAMNED === */}
            <section className="mb-10" style={{ animation: 'court-fade-in 0.8s ease-out 0.2s both' }}>
              <SectionLabel icon={Crown} label="this day's verdicts" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <VerdictCard
                  variant="sovereign"
                  title="THE SOVEREIGN"
                  subtitle="cleanest wallet judged in 24h"
                  icon={Crown}
                  data={data.sovereign}
                  emptyText="no sovereign anointed yet"
                  testid="court-sovereign"
                />
                <VerdictCard
                  variant="damned"
                  title="THE MOST DAMNED"
                  subtitle="darkest wallet judged in 24h"
                  icon={Skull}
                  data={data.most_damned}
                  emptyText="no soul condemned yet"
                  testid="court-damned"
                />
              </div>
            </section>

            {/* === LEDGER OF VERDICTS === */}
            <section className="mb-10" style={{ animation: 'court-fade-in 0.8s ease-out 0.3s both' }}>
              <SectionLabel icon={ShieldCheck} label="ledger of verdicts · token calls" />
              <LedgerPanel ledgerStats={data.ledger_stats} tokenRoll={data.token_roll} />
            </section>

            {/* === SOUL OATHS === */}
            <section className="mb-10" style={{ animation: 'court-fade-in 0.8s ease-out 0.35s both' }}>
              <SectionLabel icon={Lock} label="oaths sworn before the chancellor" />
              <OathsPanel oaths={oaths} />
            </section>

            {/* === FRESH VERDICTS === */}
            <section className="mb-10" style={{ animation: 'court-fade-in 0.8s ease-out 0.4s both' }}>
              <SectionLabel icon={Activity} label="fresh verdicts" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {(data.fresh_verdicts || []).length === 0 ? (
                  <div className="col-span-full text-center text-[11px] font-mono tracking-[0.32em] uppercase py-10"
                    style={{ color: 'rgba(230,220,200,0.35)' }}>
                    the court has just opened. no verdicts yet.
                  </div>
                ) : (data.fresh_verdicts || []).map((v) => (
                  <a key={v.card_id}
                    href={`${API_URL}/api/chancellor/share/${v.card_id}`}
                    target="_blank" rel="noreferrer"
                    data-testid={`fresh-verdict-${v.card_id}`}
                    className="group relative px-4 py-3 transition-all hover:scale-[1.01] flex items-center gap-3"
                    style={{
                      background: 'linear-gradient(180deg, rgba(30,8,6,0.6) 0%, rgba(15,4,3,0.7) 100%)',
                      border: '1px solid rgba(200,144,72,0.18)',
                      borderRadius: '2px',
                      textDecoration: 'none',
                    }}>
                    <span className="inline-block w-2 h-2 rounded-full shrink-0"
                      style={{
                        background: TIER_COLORS[v.tier] || '#a0a0a0',
                        boxShadow: `0 0 10px ${TIER_COLORS[v.tier] || '#a0a0a0'}88`,
                      }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-mono tracking-[0.08em]"
                        style={{ color: 'rgba(230,220,200,0.85)' }}>
                        {v.wallet_short}
                      </div>
                      <div className="text-[8px] font-mono tracking-[0.28em] uppercase mt-0.5 flex items-center gap-2"
                        style={{ color: TIER_COLORS[v.tier] || 'rgba(200,144,72,0.5)' }}>
                        <span>{v.tier || 'unknown'}</span>
                        <span style={{ opacity: 0.5 }}>·</span>
                        <span style={{ color: 'rgba(200,144,72,0.55)' }}>{v.sol?.toFixed(2)} SOL</span>
                        <span style={{ opacity: 0.5 }}>·</span>
                        <span style={{ color: 'rgba(230,220,200,0.4)' }}>{shortAgo(v.created_at)} ago</span>
                      </div>
                    </div>
                    <ChevronRight className="w-3 h-3 opacity-30 group-hover:opacity-70 shrink-0"
                      style={{ color: '#c89048' }} />
                  </a>
                ))}
              </div>
            </section>

            {/* === CTA === */}
            <section className="text-center pt-8 pb-4" style={{ animation: 'court-fade-in 0.8s ease-out 0.5s both' }}>
              <Link to="/chancellor" data-testid="court-cta-back"
                className="inline-flex items-center gap-2 px-6 py-3 transition-all hover:scale-[1.02]"
                style={{
                  background: 'linear-gradient(180deg, rgba(200,144,72,0.22), rgba(140,80,30,0.14))',
                  border: '1px solid rgba(200,144,72,0.5)',
                  color: '#ffd680',
                  fontFamily: "'Cinzel', serif",
                  fontSize: '12px',
                  letterSpacing: '0.32em',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  textDecoration: 'none',
                }}>
                <Sparkles className="w-3.5 h-3.5" />
                bring me a soul to judge
              </Link>
              <p className="mt-3 text-[8px] font-mono tracking-[0.4em] uppercase"
                style={{ color: 'rgba(200,144,72,0.32)' }}>
                ◆ gas-free gremlins · $gfg ◆
              </p>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
function SectionLabel({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-3 mb-3.5">
      <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, rgba(200,144,72,0.22), transparent)' }} />
      <div className="flex items-center gap-1.5">
        <Icon className="w-3 h-3" style={{ color: 'rgba(200,144,72,0.55)' }} />
        <span className="text-[9px] font-mono tracking-[0.45em] uppercase"
          style={{ color: 'rgba(200,144,72,0.65)' }}>
          {label}
        </span>
      </div>
      <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, rgba(200,144,72,0.22), transparent)' }} />
    </div>
  );
}

function CourtStat({ label, value, testid }) {
  return (
    <div className="flex flex-col items-center gap-0.5" data-testid={testid}>
      <div className="text-[14px] font-mono tracking-[0.04em]" style={{ color: '#e6dcc8', fontWeight: 600 }}>{value}</div>
      <div className="text-[7px] font-mono tracking-[0.35em] uppercase" style={{ color: 'rgba(200,144,72,0.55)' }}>{label}</div>
    </div>
  );
}

function VerdictCard({ variant, title, subtitle, icon: Icon, data, emptyText, testid }) {
  const isSovereign = variant === 'sovereign';
  const accent = isSovereign ? '#ffd680' : '#ff7a64';
  const rim = isSovereign ? 'rgba(200,144,72,0.5)' : 'rgba(220,80,60,0.5)';
  const glow = isSovereign ? 'rgba(255,200,80,0.18)' : 'rgba(220,38,38,0.18)';
  return (
    <div data-testid={testid} className="relative px-5 py-5 flex flex-col"
      style={{
        background: isSovereign
          ? 'linear-gradient(160deg, rgba(50,30,8,0.65) 0%, rgba(20,8,4,0.78) 100%)'
          : 'linear-gradient(160deg, rgba(50,8,8,0.65) 0%, rgba(20,4,4,0.78) 100%)',
        border: `1px solid ${rim}`,
        borderRadius: '3px',
        boxShadow: `0 0 28px ${glow}, 0 6px 22px rgba(0,0,0,0.6)`,
        minHeight: '210px',
      }}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <span className="text-[8px] font-mono tracking-[0.45em] uppercase" style={{ color: accent }}>
          {title}
        </span>
      </div>
      <div className="text-[8px] font-mono tracking-[0.32em] uppercase mb-3"
        style={{ color: 'rgba(230,220,200,0.4)' }}>
        {subtitle}
      </div>

      {!data ? (
        <div className="flex-1 flex items-center justify-center text-[10px] font-mono tracking-[0.32em] uppercase"
          style={{ color: 'rgba(230,220,200,0.32)' }}>
          {emptyText}
        </div>
      ) : (
        <div className="flex-1 flex flex-col justify-between">
          <div>
            <div style={{
              fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
              fontSize: '20px',
              color: accent,
              letterSpacing: '0.12em',
              fontWeight: 700,
              textShadow: `0 0 14px ${glow}`,
            }} data-testid={`${testid}-wallet`}>
              {data.wallet_short}
            </div>
            <div className="mt-1 text-[8px] font-mono tracking-[0.28em] uppercase"
              style={{ color: TIER_COLORS[data.tier] || 'rgba(200,144,72,0.5)' }}>
              {data.tier || 'unknown'} tier
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-4">
            <Stat label="SOL balance" value={`◎ ${(data.sol || 0).toFixed(2)}`} />
            <Stat label="tokens held" value={data.tokens || 0} />
            {data.portfolio_usd > 0 && (
              <Stat label="token value" value={CompactUSD(data.portfolio_usd)} />
            )}
            {data.concentration != null && (
              <Stat label="top holding" value={`${data.concentration}%`} />
            )}
            {data.nfts > 0 && <Stat label="NFTs" value={data.nfts} />}
            {data.stable_pct != null && <Stat label="stables" value={`${data.stable_pct}%`} />}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[7.5px] font-mono tracking-[0.32em] uppercase"
        style={{ color: 'rgba(200,144,72,0.5)' }}>
        {label}
      </div>
      <div className="text-[12px] font-mono tracking-[0.04em] mt-0.5"
        style={{ color: '#e6dcc8', fontWeight: 600 }}>
        {value}
      </div>
    </div>
  );
}

function LedgerPanel({ ledgerStats, tokenRoll }) {
  const stats = ledgerStats || {};
  const hasEvaluations = (stats.evaluated || 0) > 0;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">

      {/* Track record summary card */}
      <div data-testid="ledger-summary" className="lg:col-span-1 px-5 py-5"
        style={{
          background: 'linear-gradient(180deg, rgba(20,8,16,0.7) 0%, rgba(10,4,8,0.85) 100%)',
          border: '1px solid rgba(200,144,72,0.28)',
          borderRadius: '3px',
        }}>
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-3.5 h-3.5" style={{ color: 'rgba(200,144,72,0.7)' }} />
          <span className="text-[8px] font-mono tracking-[0.45em] uppercase"
            style={{ color: 'rgba(200,144,72,0.7)' }}>
            track record
          </span>
        </div>
        {hasEvaluations ? (
          <>
            <div style={{
              fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
              fontSize: '46px',
              color: stats.accuracy_pct >= 60 ? '#88dd88' : stats.accuracy_pct >= 40 ? '#ffd680' : '#ff7a64',
              letterSpacing: '0.06em',
              fontWeight: 700,
              lineHeight: 1,
            }}>
              {stats.accuracy_pct}<span style={{ fontSize: '20px' }}>%</span>
            </div>
            <div className="text-[8px] font-mono tracking-[0.32em] uppercase mt-1"
              style={{ color: 'rgba(230,220,200,0.45)' }}>
              accuracy on {stats.evaluated} evaluated calls
            </div>

            <div className="mt-4 space-y-1.5">
              <LedgerRow label="warned correctly" value={stats.warned_correctly} icon={ShieldAlert} positive />
              <LedgerRow label="false alarms" value={stats.warned_incorrectly} icon={ShieldAlert} />
              <LedgerRow label="favored correctly" value={stats.favored_correctly} icon={ShieldCheck} positive />
              <LedgerRow label="favors that fell" value={stats.favored_incorrectly} icon={ShieldCheck} />
            </div>
          </>
        ) : (
          <>
            <div style={{
              fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
              fontSize: '32px',
              color: 'rgba(200,144,72,0.85)',
              letterSpacing: '0.08em',
              fontWeight: 700,
              lineHeight: 1.1,
            }}>
              {stats.total_calls || 0}
            </div>
            <div className="text-[9px] font-mono tracking-[0.32em] uppercase mt-1"
              style={{ color: 'rgba(230,220,200,0.55)' }}>
              tokens called
            </div>
            <p className="mt-4 text-[10px] leading-relaxed"
              style={{ color: 'rgba(230,220,200,0.55)', fontFamily: "'Cinzel', serif" }}>
              The Chancellor logs every verdict. After 24 hours, each call is measured against actual price. His accuracy is public, his shame visible.
            </p>
            <div className="mt-3 text-[8px] font-mono tracking-[0.32em] uppercase"
              style={{ color: 'rgba(200,144,72,0.45)' }}>
              · evaluations pending ·
            </div>
          </>
        )}
      </div>

      {/* Recent token calls list */}
      <div className="lg:col-span-2 px-5 py-5"
        style={{
          background: 'linear-gradient(180deg, rgba(20,8,8,0.6) 0%, rgba(10,4,4,0.78) 100%)',
          border: '1px solid rgba(200,144,72,0.22)',
          borderRadius: '3px',
        }}>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-3.5 h-3.5" style={{ color: 'rgba(200,144,72,0.7)' }} />
          <span className="text-[8px] font-mono tracking-[0.45em] uppercase"
            style={{ color: 'rgba(200,144,72,0.7)' }}>
            recent token verdicts
          </span>
        </div>
        {(tokenRoll || []).length === 0 ? (
          <div className="text-[10px] font-mono tracking-[0.32em] uppercase py-6 text-center"
            style={{ color: 'rgba(230,220,200,0.32)' }}>
            no tokens investigated yet
          </div>
        ) : (
          <div className="space-y-1">
            {(tokenRoll || []).map((t) => {
              const seed = (t.verdict_seed || '').toUpperCase();
              const verdictColor =
                seed.startsWith('AVOID') ? '#ff5a5a' :
                seed.startsWith('CAUTION') ? '#ffaa50' :
                seed.startsWith('TOLERABLE') ? '#dcc878' :
                seed.startsWith('CLEAN') ? '#88dd88' : 'rgba(200,144,72,0.7)';
              return (
                <div key={t.call_id}
                  data-testid={`token-call-${t.call_id}`}
                  className="flex items-center gap-3 px-3 py-2"
                  style={{ border: '1px solid rgba(200,144,72,0.10)', borderRadius: '2px' }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span style={{
                        fontFamily: "'Cinzel', serif",
                        fontSize: '13px',
                        color: '#ffd680',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                      }}>${t.symbol}</span>
                      <span className="text-[9px] font-mono tracking-[0.04em] truncate"
                        style={{ color: 'rgba(230,220,200,0.4)', maxWidth: '200px' }}>
                        {t.name}
                      </span>
                    </div>
                    <div className="text-[8px] font-mono tracking-[0.04em] mt-0.5"
                      style={{ color: 'rgba(200,144,72,0.5)' }}>
                      {t.price_at_call != null ? CompactUSD(t.price_at_call) : '—'} · {shortAgo(t.called_at)} ago
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-0.5 shrink-0">
                    <span className="text-[8px] font-mono tracking-[0.32em] uppercase"
                      style={{ color: verdictColor, fontWeight: 700 }}>
                      {seed.split('.')[0] || 'noted'}
                    </span>
                    <span className="text-[7.5px] font-mono tracking-[0.18em] uppercase"
                      style={{ color: 'rgba(230,220,200,0.4)' }}>
                      risk {t.rug_score ?? '?'}/100
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function LedgerRow({ label, value, icon: Icon, positive }) {
  const v = value || 0;
  return (
    <div className="flex items-center justify-between text-[10px]">
      <div className="flex items-center gap-1.5">
        <Icon className="w-3 h-3" style={{ color: positive ? 'rgba(140,220,160,0.65)' : 'rgba(220,80,60,0.65)' }} />
        <span style={{ color: 'rgba(230,220,200,0.65)', fontFamily: "'Cinzel', serif" }}>{label}</span>
      </div>
      <span className="font-mono"
        style={{ color: positive ? 'rgba(140,220,160,0.95)' : 'rgba(220,80,60,0.85)', fontWeight: 700 }}>
        {v}
      </span>
    </div>
  );
}

function OathsPanel({ oaths }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const o = oaths || { oaths: [], active: 0, kept_total: 0, broken_total: 0 };

  // Allow re-render on swear success — bump tick so parent reload happens via interval
  useEffect(() => {}, [refreshTick]);

  return (
    <>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
      {/* Stats card */}
      <div data-testid="oaths-summary" className="lg:col-span-1 px-5 py-5"
        style={{
          background: 'linear-gradient(180deg, rgba(20,8,16,0.7) 0%, rgba(10,4,8,0.85) 100%)',
          border: '1px solid rgba(200,144,72,0.28)',
          borderRadius: '3px',
        }}>
        <div className="flex items-center gap-2 mb-3">
          <Flame className="w-3.5 h-3.5" style={{ color: 'rgba(255,170,80,0.7)' }} />
          <span className="text-[8px] font-mono tracking-[0.45em] uppercase"
            style={{ color: 'rgba(200,144,72,0.7)' }}>
            oaths in motion
          </span>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4">
          <OathStat label="active" value={o.active} color="#ffd680" testid="oath-stat-active" />
          <OathStat label="kept" value={o.kept_total} color="#88dd88" testid="oath-stat-kept" />
          <OathStat label="broken" value={o.broken_total} color="#ff7a64" testid="oath-stat-broken" />
        </div>

        <p className="mt-5 text-[9.5px] leading-relaxed"
          style={{ color: 'rgba(230,220,200,0.55)', fontFamily: "'Cinzel', serif" }}>
          Souls swear public oaths before the Chancellor. Diamond-hand vows watched daily. Break the word: public shame. Keep it: public glory.
        </p>

        <button onClick={() => setModalOpen(true)}
          data-testid="swear-oath-cta"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 transition-all hover:scale-[1.01]"
          style={{
            background: 'linear-gradient(180deg, rgba(255,170,80,0.22), rgba(180,80,30,0.14))',
            border: '1px solid rgba(255,170,80,0.5)',
            color: '#ffd680',
            fontFamily: "'Cinzel', serif",
            fontSize: '11px',
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            fontWeight: 700,
            cursor: 'pointer',
            borderRadius: '2px',
          }}>
          <Flame className="w-3.5 h-3.5" />
          swear an oath
        </button>
      </div>

      {/* Oaths list */}
      <div className="lg:col-span-2 px-5 py-5"
        style={{
          background: 'linear-gradient(180deg, rgba(20,8,8,0.6) 0%, rgba(10,4,4,0.78) 100%)',
          border: '1px solid rgba(200,144,72,0.22)',
          borderRadius: '3px',
        }}>
        <div className="flex items-center gap-2 mb-3">
          <Lock className="w-3.5 h-3.5" style={{ color: 'rgba(200,144,72,0.7)' }} />
          <span className="text-[8px] font-mono tracking-[0.45em] uppercase"
            style={{ color: 'rgba(200,144,72,0.7)' }}>
            recent vows
          </span>
        </div>
        {(o.oaths || []).length === 0 ? (
          <div className="text-[10px] font-mono tracking-[0.32em] uppercase py-6 text-center"
            style={{ color: 'rgba(230,220,200,0.32)' }}>
            no soul has yet sworn upon the court
          </div>
        ) : (
          <div className="space-y-1.5">
            {(o.oaths || []).map((vow) => {
              const v = (vow.verdict || 'PENDING').toUpperCase();
              const colorMap = { PENDING: '#ffd680', KEPT: '#88dd88', BROKEN: '#ff7a64' };
              const color = colorMap[v] || '#c89048';
              return (
                <div key={vow.oath_id}
                  data-testid={`oath-${vow.oath_id}`}
                  className="flex items-center gap-3 px-3 py-2"
                  style={{ border: `1px solid ${color}33`, borderRadius: '2px' }}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{
                    background: color,
                    boxShadow: `0 0 6px ${color}88`,
                    animation: v === 'PENDING' ? 'court-pulse 2s ease-in-out infinite' : 'none',
                  }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] font-mono tracking-[0.06em]"
                        style={{ color: 'rgba(230,220,200,0.85)' }}>
                        {vow.wallet_short}
                      </span>
                      <span className="text-[9px] font-mono tracking-[0.18em] uppercase"
                        style={{ color: 'rgba(200,144,72,0.55)' }}>
                        diamond-hand
                      </span>
                      <span style={{ color: '#ffd680', fontFamily: "'Cinzel', serif", fontSize: '12px', fontWeight: 700 }}>
                        ${vow.target_symbol}
                      </span>
                    </div>
                    <div className="text-[8px] font-mono tracking-[0.18em] uppercase mt-0.5"
                      style={{ color: 'rgba(230,220,200,0.45)' }}>
                      {(vow.snapshot_balance || 0).toLocaleString(undefined, {maximumFractionDigits: 2})} sworn · {vow.days}d vow
                    </div>
                  </div>
                  <span className="text-[8px] font-mono tracking-[0.32em] uppercase shrink-0"
                    style={{ color, fontWeight: 700 }}>
                    {v}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
    {modalOpen && <SwearOathModal onClose={() => setModalOpen(false)} onSworn={() => { setModalOpen(false); setRefreshTick(t => t + 1); window.location.reload(); }} />}
    </>
  );
}

function SwearOathModal({ onClose, onSworn }) {
  const [wallet, setWallet] = useState('');
  const [mint, setMint] = useState('');
  const [days, setDays] = useState(7);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async () => {
    setError('');
    if (!wallet.trim() || !mint.trim()) {
      setError('all fields required');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/chancellor/oaths/swear`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet: wallet.trim(),
          target_token_mint: mint.trim(),
          oath_type: 'DIAMOND_HAND',
          days,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'oath rejected');
      setSuccess(d);
      setTimeout(() => { onSworn && onSworn(); }, 2200);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="swear-oath-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(5,2,8,0.92)', backdropFilter: 'blur(8px)', animation: 'court-fade-in 0.3s ease-out' }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md"
        style={{
          background: 'linear-gradient(180deg, rgba(30,8,6,0.95) 0%, rgba(15,4,3,0.98) 100%)',
          border: '1px solid rgba(255,170,80,0.5)',
          borderRadius: '3px',
          boxShadow: '0 0 50px rgba(255,170,80,0.18), 0 12px 40px rgba(0,0,0,0.8)',
        }}>
        <button onClick={onClose} data-testid="swear-oath-close"
          className="absolute top-3 right-3 z-10"
          style={{ color: 'rgba(200,144,72,0.5)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 10px', fontSize: '13px' }}>
          × close
        </button>

        <div className="px-6 sm:px-8 pt-7 pb-6">
          <div className="text-center mb-5">
            <h2 style={{
              fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
              fontSize: '20px',
              color: '#ffd680',
              letterSpacing: '0.28em',
              textShadow: '0 0 20px rgba(255,170,80,0.35)',
            }}>
              SWEAR AN OATH
            </h2>
            <p className="mt-1 text-[9px] font-mono tracking-[0.32em] uppercase"
              style={{ color: 'rgba(255,170,80,0.65)' }}>
              · diamond-hand vow ·
            </p>
          </div>

          {success ? (
            <div className="text-center py-8" data-testid="swear-oath-success">
              <div style={{ fontSize: '48px', color: '#ffd680', fontFamily: "'Cinzel Decorative', serif", lineHeight: 1 }}>⚖</div>
              <p className="mt-3 text-[12px] font-serif tracking-[0.04em]" style={{ color: '#e6dcc8' }}>
                Your oath upon <span style={{ color: '#ffd680', fontWeight: 700 }}>${success.target_symbol}</span> is bound.
              </p>
              <p className="mt-1 text-[9px] font-mono tracking-[0.18em] uppercase" style={{ color: 'rgba(200,144,72,0.65)' }}>
                {(success.snapshot_balance || 0).toLocaleString(undefined, {maximumFractionDigits: 2})} sworn · {success.days}d watch
              </p>
              <p className="mt-4 text-[8px] font-mono tracking-[0.32em] uppercase" style={{ color: 'rgba(230,220,200,0.4)' }}>
                the chancellor watches now.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                <Field label="wallet to bind">
                  <input type="text" value={wallet}
                    onChange={(e) => setWallet(e.target.value)}
                    placeholder="paste solana wallet address"
                    data-testid="swear-oath-wallet"
                    className="w-full bg-transparent outline-none font-mono text-xs px-3 py-2"
                    style={{ color: '#e6dcc8', border: '1px solid rgba(200,144,72,0.22)', borderRadius: '2px', background: 'rgba(8,2,4,0.55)' }} />
                </Field>
                <Field label="token mint to swear upon">
                  <input type="text" value={mint}
                    onChange={(e) => setMint(e.target.value)}
                    placeholder="paste token mint address"
                    data-testid="swear-oath-mint"
                    className="w-full bg-transparent outline-none font-mono text-xs px-3 py-2"
                    style={{ color: '#e6dcc8', border: '1px solid rgba(200,144,72,0.22)', borderRadius: '2px', background: 'rgba(8,2,4,0.55)' }} />
                </Field>
                <Field label={`days of watch · ${days}`}>
                  <input type="range" value={days} min={1} max={30}
                    onChange={(e) => setDays(parseInt(e.target.value, 10) || 7)}
                    data-testid="swear-oath-days"
                    className="w-full accent-amber-500" />
                </Field>
              </div>

              {error && (
                <p className="mt-3 text-[10px] font-mono tracking-[0.18em]" data-testid="swear-oath-error"
                  style={{ color: 'rgba(220,80,60,0.85)' }}>
                  ⚠ {error}
                </p>
              )}

              <button onClick={submit} disabled={submitting || !wallet.trim() || !mint.trim()}
                data-testid="swear-oath-submit"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 transition-all"
                style={{
                  background: !submitting && wallet.trim() && mint.trim()
                    ? 'linear-gradient(180deg, rgba(255,170,80,0.28), rgba(180,80,30,0.18))'
                    : 'rgba(40,20,12,0.4)',
                  border: '1px solid rgba(255,170,80,0.5)',
                  color: !submitting && wallet.trim() && mint.trim() ? '#ffd680' : 'rgba(200,144,72,0.4)',
                  cursor: !submitting && wallet.trim() && mint.trim() ? 'pointer' : 'not-allowed',
                  fontFamily: "'Cinzel', serif",
                  fontSize: '12px',
                  letterSpacing: '0.32em',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  borderRadius: '2px',
                }}>
                {submitting ? 'binding...' : '⚖ bind the oath ⚖'}
              </button>

              <p className="mt-3 text-center text-[8px] font-mono tracking-[0.32em] uppercase"
                style={{ color: 'rgba(230,220,200,0.4)' }}>
                the chancellor takes a snapshot. sell more than 5% — you are damned.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[8px] font-mono tracking-[0.32em] uppercase mb-1.5"
        style={{ color: 'rgba(200,144,72,0.55)' }}>
        {label}
      </div>
      {children}
    </label>
  );
}

function OathStat({ label, value, color, testid }) {
  return (
    <div data-testid={testid} className="text-center">
      <div style={{
        fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
        fontSize: '32px',
        color,
        fontWeight: 700,
        lineHeight: 1,
        textShadow: `0 0 14px ${color}55`,
      }}>
        {value || 0}
      </div>
      <div className="text-[8px] font-mono tracking-[0.32em] uppercase mt-1"
        style={{ color: 'rgba(230,220,200,0.5)' }}>
        {label}
      </div>
    </div>
  );
}
